import type { Logger } from "@expert/logging";

import { type QueryFunctionContext, type UndefinedInitialDataOptions, useQuery } from "@tanstack/react-query";

import { getGamificationApiClient } from "@/sales-tools/home-product/api/api";
import { useLogger } from "@/sdk";
import { userCache } from "@/shared-utils";

const ADHERENCE_THRESHOLD = 0;

interface ChecklistSummaryQueryProps extends Partial<
    UndefinedInitialDataOptions<
        ChecklistSummaryStats | undefined,
        Error,
        ChecklistSummaryStats | undefined,
        ChecklistSummaryStatsKey
    >
> {
    params: ChecklistSummaryStatsParams;
}

interface ChecklistSummaryStatsParams {
    start: string;
    end: string;
}

interface ChecklistSummaryRow {
    behavior: string;
    isInvalidData: boolean;
    isWarning: boolean;
    percent: number;
}

export interface ChecklistSummaryStatsResponse {
    totalEligibleSessions: number;
    checklistSummary: { behavior: string; count: number; title: string }[];
}

export interface ChecklistSummaryStats {
    checklistSummaryRows: Array<ChecklistSummaryRow>;
    sessionCount: number;
    overallAdherencePercentDisplay: string;
}

export function processChecklistSummaryData(
    stats: ChecklistSummaryStatsResponse,
    adherenceThreshold: number,
): ChecklistSummaryStats {
    const sessionCount = stats?.totalEligibleSessions || 0;
    const checklistSummaryRows = (stats?.checklistSummary || []).map(({ title, count }) => {
        const percent = Math.round((count / sessionCount) * 100);

        const isInvalidData = sessionCount === 0;
        const isWarning = percent <= adherenceThreshold;

        return {
            behavior: title,
            isInvalidData,
            isWarning,
            percent,
        };
    });
    const overallAdherencePercent =
        checklistSummaryRows.reduce((total, { percent }) => total + percent, 0) / checklistSummaryRows.length;
    const overallAdherencePercentDisplay =
        Number.isNaN(overallAdherencePercent) || sessionCount === 0 ? "N/A" : `${Math.round(overallAdherencePercent)}%`;

    return {
        checklistSummaryRows,
        sessionCount,
        overallAdherencePercentDisplay,
    };
}

export type ChecklistSummaryStatsKey = [string, ChecklistSummaryStatsParams, string | null];

export function useChecklistSummaryQuery({ params, ...rest }: ChecklistSummaryQueryProps) {
    const logger = useLogger({ module: "useChecklistSummaryQuery", supportTeam: "sales" });

    return useQuery({
        queryKey: ["checklist-summary", params, userCache.employeeId],
        queryFn: (context) => getChecklistSummaryStats(logger, context),
        ...rest,
    });
}

export async function getChecklistSummaryStats(
    logger: Logger,
    { queryKey: [, { end, start }] }: QueryFunctionContext<ChecklistSummaryStatsKey>,
): Promise<ChecklistSummaryStats | undefined> {
    try {
        logger.info({ start, end }, "Fetching checklist summary stats");
        const params = new URLSearchParams({ start, end });

        const result = await getGamificationApiClient()
            .get(`v1/metrics/checklist-summary?${params}`)
            .json<ChecklistSummaryStatsResponse>();

        return processChecklistSummaryData(result, ADHERENCE_THRESHOLD);
    } catch (error) {
        logger.warn({ error }, "Error fetching checklist summary stats");
    }
}
